import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'

type DocReferenceOverlayProps = {
  imageNatural: { w: number; h: number }
  firstPageWidth?: number
  isMultipage?: boolean
  onDocReferenceClick?: (highlight: AliasHighlight) => void
  onDocReferenceHover?: (highlight: AliasHighlight | null) => void
}

// Amber + dashed: visually distinct from the purple asset/alias highlights —
// these boxes navigate to another document rather than select a subject.
const FILL = 'rgba(245, 158, 11, 0.18)'
const STROKE = 'rgba(217, 119, 6, 0.95)'
const PADDING = 2

function getPageLayout(
  isRightPage: boolean,
  imageNatural: { w: number; h: number },
  firstPageWidth: number | undefined,
  isMultipage: boolean | undefined
): { pageWidth: number; xOffset: number } {
  if (!isMultipage || !firstPageWidth) {
    return { pageWidth: imageNatural.w, xOffset: 0 }
  }
  if (isRightPage) {
    return {
      pageWidth: imageNatural.w - firstPageWidth,
      xOffset: firstPageWidth,
    }
  }
  return { pageWidth: firstPageWidth, xOffset: 0 }
}

function DocReferenceRect({
  highlight,
  pageWidth,
  xOffset,
  imageHeight,
  onClick,
  onHover,
}: {
  highlight: AliasHighlight
  pageWidth: number
  xOffset: number
  imageHeight: number
  onClick: () => void
  onHover: (highlight: AliasHighlight | null) => void
}) {
  const bbox: NormalizedBBox = highlight.bbox
  return (
    <rect
      x={xOffset + bbox.left * pageWidth - PADDING}
      y={bbox.top * imageHeight - PADDING}
      width={bbox.width * pageWidth + PADDING * 2}
      height={bbox.height * imageHeight + PADDING * 2}
      rx={2}
      ry={2}
      fill={FILL}
      stroke={STROKE}
      strokeWidth={1.5}
      strokeDasharray='4,3'
      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
      onClick={onClick}
      onMouseEnter={() => onHover(highlight)}
      onMouseLeave={() => onHover(null)}>
      <title>{`Reference: ${highlight.label}`}</title>
    </rect>
  )
}

/**
 * Doc-reference (off-page connector / drawing number) boxes on the current
 * page. Unlike {@link AliasHighlightOverlay}'s asset boxes, clicking one
 * navigates the viewer to the referenced document; hover pre-resolves the
 * target so the click can navigate without waiting.
 */
export function DocReferenceOverlay({
  imageNatural,
  firstPageWidth,
  isMultipage,
  onDocReferenceClick,
  onDocReferenceHover,
}: DocReferenceOverlayProps) {
  const { docReferenceHighlights, pageNumber } = useDocumentTabDataContext()

  const visibleHighlights = docReferenceHighlights.filter(
    (highlight) =>
      highlight.page === pageNumber ||
      (isMultipage && highlight.page === pageNumber + 1)
  )

  if (visibleHighlights.length === 0) return null

  return (
    <g>
      {visibleHighlights.map((highlight, idx) => {
        const isRightPage = isMultipage === true && highlight.page > pageNumber
        const { pageWidth, xOffset } = getPageLayout(
          isRightPage,
          imageNatural,
          firstPageWidth,
          isMultipage
        )

        return (
          <DocReferenceRect
            key={`${highlight.subjectUid}-${highlight.aliasNorm}-${idx}`}
            highlight={highlight}
            pageWidth={pageWidth}
            xOffset={xOffset}
            imageHeight={imageNatural.h}
            onClick={() => onDocReferenceClick?.(highlight)}
            onHover={(hovered) => onDocReferenceHover?.(hovered)}
          />
        )
      })}
    </g>
  )
}
